import { useEffect, useRef, useState } from "react";
import { Info } from "lucide-react";
import { cn } from "../lib/cn";

type Props = {
  label: string;
  content: string;
  className?: string;
  iconSize?: number;
};

export default function InfoTooltip({
  label,
  content,
  className,
  iconSize = 12,
}: Props) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <span
      ref={wrapRef}
      className={cn("relative inline-flex items-center", className)}
    >
      <button
        type="button"
        aria-label={label}
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="text-fg-subtle hover:text-fg-muted focus:text-fg-muted leading-none focus:outline-none"
      >
        <Info size={iconSize} />
      </button>
      {open && (
        <span
          role="tooltip"
          className="absolute left-1/2 -translate-x-1/2 top-full mt-1.5 z-50 w-64 max-w-[80vw] whitespace-pre-line rounded-md border border-border bg-bg-raised text-fg text-[11px] leading-snug px-3 py-2 shadow-lg"
        >
          {content}
        </span>
      )}
    </span>
  );
}
